/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
/* Custom Code */
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import Block, { BlockProps, BlockVariant, BlockVariantSide } from "@tCentro/Components/Common/Block";
import blockFullWidthField from "@tCentro/Components/Common/Block/fields/fullWidth";
import gridVerticalGapField, {
    getVerticalGapData,
    VerticalGap,
} from "@tCentro/Components/Common/Block/fields/gridVerticalGap";
import blockPreserveSpacingField from "@tCentro/Components/Common/Block/fields/preserveSpacing";
import blockVariantField from "@tCentro/Components/Common/Block/fields/variant";
import blockVariantSideField from "@tCentro/Components/Common/Block/fields/variantSide";
import { gridGapStyles } from "@tCentro/theme/mixins";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Custom Code */

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    cssClass = "cssClass",
    // Custom Code
    blockPreserveSpacing = "blockPreserveSpacing",
    blockFullWidth = "blockFullWidth",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    gridVerticalGap = "gridVerticalGap",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.cssClass]: string;
        // Custom Code
        [fields.blockPreserveSpacing]: boolean;
        [fields.blockFullWidth]: boolean;
        [fields.blockVariant]: BlockVariant;
        [fields.blockVariantSide]: BlockVariantSide;
        [fields.gridVerticalGap]: VerticalGap;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    return {
        currentPage: getCurrentPage(state),
        catalogPageState,
        breadCrumbs: state.components.breadcrumbs.links,
        brandPath,
        categoryFacets: getProductListDataViewProperty(state, "properties"),
    };
};

type CustomProps = Props & HasCategoryContext & ReturnType<typeof mapStateToProps>;

const CENCustomRowforPlp: React.FunctionComponent<CustomProps> = (props: CustomProps) => {
    const {
        fields: { columns, gap, alignment, reflow },
        id,
        catalogPageState,
        categoryFacets,
        breadCrumbs,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    /* Custom Code */
    const subCategories =
        catalogPageState?.value?.properties?.subCategories &&
        JSON.parse(decodeURI(catalogPageState?.value.properties?.subCategories));

    const categoryFacetsData = JSON.parse(categoryFacets?.categoryList ?? "[]");
    const lastLevelCategory = breadCrumbs?.[breadCrumbs?.length - 1]?.children;
    const categoryFacetsDataNew = categoryFacetsData?.filter((item: any) => item.Name !== lastLevelCategory);

    const {
        fields: { blockPreserveSpacing, blockFullWidth, blockVariant, blockVariantSide, gridVerticalGap, cssClass },
    } = props;

    const blockProps: BlockProps = {
        preserveSpacing: blockPreserveSpacing,
        fullWidth: blockFullWidth,
        variant: blockVariant,
        variantSide: blockVariantSide,
    };

    const gridContainerProps = {
        gap: gridVerticalGap ? 0 : gap || 0,
        css: css`
            ${gridVerticalGap ? gridGapStyles([gap ? `${gap}px` : 0], getVerticalGapData(gridVerticalGap)) : null}

            &.taxonomyCarousel {
                margin-top: 16px !important;
                margin-bottom: ${subCategories?.length === 0 ? "0px;" : "25px;"};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-bottom: 0px;
                        `,
                        css`
                            margin-bottom: 0px;
                        `,
                        css`
                            margin-bottom: 0px;
                        `,
                        null,
                        null,
                    ])}
                > div[class^="GridOffset"]:first-child {
                    > div[class^="GridItemStyle"]:first-child {
                        @media (min-width: 992px) and (max-width: 1024px) {
                            flex-basis: 70% !important;
                            max-width: 70% !important;
                        }
                    }
                    > div[class^="GridItemStyle"]:nth-child(2) {
                        @media (min-width: 992px) and (max-width: 1024px) {
                            flex-basis: 30% !important;
                            max-width: 30% !important;
                        }
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                gap: 16px;
                            `,
                            css`
                                gap: 16px;
                            `,
                            css`
                                gap: 16px;
                            `,
                            null,
                            null,
                        ])}
                }
                div[class^="Wrapper"] {
                    height: ${subCategories?.length === 0 || !categoryFacetsDataNew ? "auto;" : "100%;"};
                }
            }
        `,
    };

    return (
        <Block {...blockProps}>
            <GridContainer className={cssClass} {...gridContainerProps}>
                {columnElements}
            </GridContainer>
        </Block>
    );
    /* Custom Code */
};
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENCustomRowforPlp),
    definition: {
        group: "Basic",
        displayName: " CEN Custom Row for Plp",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            /* Custom Code */
            {
                ...blockVariantField,
                name: fields.blockVariant,
                sortOrder: 100,
                tab: advancedTab,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
                sortOrder: 101,
                tab: advancedTab,
            },
            {
                ...gridVerticalGapField,
                name: fields.gridVerticalGap,
                tooltip: `${gridVerticalGapField.tooltip} (Default: Uses the \"Gap\" configuration)`,
                sortOrder: 102,
                tab: advancedTab,
            },
            {
                ...blockFullWidthField,
                name: fields.blockFullWidth,
                sortOrder: 103,
                tab: advancedTab,
            },
            {
                ...blockPreserveSpacingField,
                name: fields.blockPreserveSpacing,
                sortOrder: 104,
                tab: advancedTab,
            },
            /* Custom Code */
        ],
    },
};

export default widgetModule;
